"use client";

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { WishlistService } from "@services/wishlistService";
import { getClientCookie } from "@lib/cookies.client";

export interface TypeWishlistCar {
  id: string;
  make: string;
  name: string;
  image?: string;
  status?: any;
}
export interface TypeWishlistExportOpinion {
  id?: string;
  ratting: number;
  text: string;
  author: string;
  authorRole: string;
  avatar: string;
  highlightImage: string;
  highlighText: string;
}

export interface TypeWishlistCarModal {
  heroImage: string;
  expressDelivery: boolean;
  modelName: string;
  brandName: string;
  cityName: string;
  minPrice: number;
  savings: number;
  expressDeliveryDate: string;
}
export interface TypeWishListGalleryMedia {
  id?: string;
  fileName: string;
  fileType: "IMAGE" | "VIDEO" | string;
  s3Url: string;
  colorId: number;
  altTag: string | null;
  metadata: any | null;
  createdAt: string;
  tagNames: string[];
  locked: boolean;
  standOut: boolean;
}
export interface TypeCarUpdateEvent {
  type: string;
  text: string;
  date: string;
}

interface WishlistContextState {
  wishlistItems: TypeWishlistCar[];
  isLoading: boolean;
  error: string | null;
  maxLimit: number;
  exportOpinion: TypeWishlistExportOpinion | null;
  selected: TypeWishlistCar | null;
}

interface WishlistContextValue extends WishlistContextState {
  initializeWishlist: () => Promise<void>;
  toggleWishlistItem: (car: TypeWishlistCar) => Promise<{
    success: boolean;
    limitReached?: boolean;
  }>;
  replaceWishlistItem: (
    oldCarId: string,
    newCar: TypeWishlistCar
  ) => Promise<boolean>;
  isCarWishlisted: (carId: string) => boolean;
  removeWishListItem: () => Promise<{
    success: boolean;
  }>;
  toggleWishListSelectedItem: (car: TypeWishlistCar) => boolean;
}

const WishlistContext = createContext<WishlistContextValue | undefined>(
  undefined
);
function transformToWishlist(item: any): TypeWishlistCar {
  return {
    id: item.slug,
    make: item.brand,
    name: item.model,
  } as TypeWishlistCar;
}

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, setState] = useState<WishlistContextState>({
    wishlistItems: [],
    isLoading: false,
    error: null,
    maxLimit: 5,
    exportOpinion: null,
    selected: null,
  });

  // Check if user is logged in
  const isLoggedIn = useCallback(() => {
    return Boolean(getClientCookie("ackodrive_tokens"));
  }, []);

  const initializeWishlist = useCallback(async () => {
    if (!isLoggedIn()) {
      setState((prev) => ({
        ...prev,
        wishlistItems: [],
        isLoading: false,
        error: null,
        selected: null,
      }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));
      const response = await WishlistService.fetchUserWishlist();
      const wishlistItems: TypeWishlistCar[] = [];
      response.data.forEach((item) => {
        wishlistItems.push(transformToWishlist(item));
      });
      setState({
        wishlistItems,
        maxLimit: 5,
        isLoading: false,
        error: null,
        exportOpinion: null,
        selected: wishlistItems[0] || null,
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: "Failed to fetch wishlist",
      }));
    }
  }, [isLoggedIn]);

  useEffect(() => {
    initializeWishlist();
  }, [initializeWishlist]);

  // Check for authentication changes and reinitialize
  useEffect(() => {
    let previousAuth = isLoggedIn();

    const handleAuthChange = () => {
      const currentlyLoggedIn = isLoggedIn();
      if (currentlyLoggedIn !== previousAuth) {
        previousAuth = currentlyLoggedIn;
        if (!currentlyLoggedIn) {
          setState((prev) => ({
            ...prev,
            wishlistItems: [],
            isLoading: false,
            error: null,
            selected: null,
          }));
        } else {
          initializeWishlist();
        }
      }
    };

    window.addEventListener("focus", handleAuthChange);

    return () => {
      window.removeEventListener("focus", handleAuthChange);
    };
  }, [
    state.wishlistItems.length,
    state.isLoading,
    isLoggedIn,
    initializeWishlist,
  ]);

  const isCarWishlisted = useCallback(
    (carId: string) => {
      return state.wishlistItems.some((item) => item.id === carId);
    },
    [state.wishlistItems]
  );

  const toggleWishlistItem = useCallback(
    async (car: TypeWishlistCar) => {
      if (!isLoggedIn()) {
        setState((prev) => ({
          ...prev,
          error: "Please log in to manage your wishlist",
        }));
        return { success: false };
      }

      try {
        if (isCarWishlisted(car.id)) {
          const response = await WishlistService.removeFromWishlist({
            slug: car.id,
            productType: "CAR",
          });
          if (response.status === "success" && response.updatedWishlist) {
            setState((prev) => ({
              ...prev,
              wishlistItems: Array.isArray(response.updatedWishlist)
                ? response.updatedWishlist.map(transformToWishlist)
                : prev.wishlistItems,
              selected:
                prev.selected ||
                (Array.isArray(response.updatedWishlist) &&
                  response.updatedWishlist.length > 0
                  ? transformToWishlist(response.updatedWishlist[0])
                  : null),
            }));
            return { success: true };
          }
          return { success: false };
        } else {
          const canAdd = await WishlistService.canAdd();
          if (canAdd === false) {
            return { success: false, limitReached: true };
          }
          const response = await WishlistService.addToWishlist({
            slug: car.id,
            productType: "CAR",
          });
          if (response.status === "success" && response.updatedWishlist) {
            setState((prev) => ({
              ...prev,
              wishlistItems: Array.isArray(response.updatedWishlist)
                ? response.updatedWishlist.map(transformToWishlist)
                : prev.wishlistItems,
              selected:
                prev.selected ||
                (Array.isArray(response.updatedWishlist) &&
                  response.updatedWishlist.length > 0
                  ? transformToWishlist(response.updatedWishlist[0])
                  : null),
            }));
            return { success: true };
          }
          // If the response indicates the limit was reached
          if (response.message === "Wishlist limit reached") {
            return { success: false, limitReached: true };
          }
          return { success: false };
        }
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: "Failed to update wishlist",
        }));
        return { success: false };
      }
    },
    [state.wishlistItems, isCarWishlisted, isLoggedIn]
  );

  const removeWishListItem = useCallback(async () => {
    if (!isLoggedIn()) {
      setState((prev) => ({
        ...prev,
        error: "Please log in to manage your wishlist",
      }));
      return { success: false };
    }

    try {
      if (state.selected !== null && state.wishlistItems.length > 0) {
        const index = state.wishlistItems.findIndex(
          (item) => item.id === state.selected?.id
        );

        const updatedList = state.wishlistItems.filter(
          (item) => item.id !== state.selected?.id
        );

        if (updatedList.length > 0) {
          const newIndex =
            index >= updatedList.length ? updatedList.length - 1 : index;
          setState((prev) => ({
            ...prev,
            wishlistItems: updatedList,
            selected: updatedList[newIndex],
          }));
        } else {
          setState((prev) => ({
            ...prev,
            wishlistItems: updatedList,
            selected: null,
          }));
        }
      }

      return { success: true };
    } catch (error) {
      setState((prev) => ({
        ...prev,
        error: "Failed to update wishlist",
      }));
      return { success: false };
    }
  }, [state.wishlistItems, isCarWishlisted, isLoggedIn]);

  const replaceWishlistItem = useCallback(
    async (oldCarId: string, newCar: TypeWishlistCar): Promise<boolean> => {
      if (!isLoggedIn()) {
        setState((prev) => ({
          ...prev,
          error: "Please log in to manage your wishlist",
        }));
        return false;
      }

      try {
        const response = await WishlistService.replaceInWishlist({
          existing: {
            slug: oldCarId,
            productType: "CAR",
          },
          incoming: {
            slug: newCar.id,
            productType: "CAR",
          },
        });

        if (response.status === "success" && response.updatedWishlist) {
          setState((prev) => ({
            ...prev,
            wishlistItems: Array.isArray(response.updatedWishlist)
              ? response.updatedWishlist.map(transformToWishlist)
              : prev.wishlistItems,
          }));
          return true;
        }
        return false;
      } catch (error) {
        setState((prev) => ({
          ...prev,
          error: "Failed to replace wishlist item",
        }));
        return false;
      }
    },
    [isLoggedIn]
  );

  const toggleWishListSelectedItem = useCallback(
    (car: TypeWishlistCar) => {
      try {
        setState((prev) => {
          return {
            ...prev,
            selected: car,
          };
        });
        return true;
      } catch (e) {
        setState((prev) => ({
          ...prev,
          error: "Failed to selected item",
        }));
        return false;
      }
    },
    [state.wishlistItems, state.selected]
  );

  const value: WishlistContextValue = {
    ...state,
    initializeWishlist,
    toggleWishlistItem,
    replaceWishlistItem,
    isCarWishlisted,
    removeWishListItem,
    toggleWishListSelectedItem,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error("useWishlist must be used within a WishlistProvider");
  }
  return context;
};
